// ============================================
// Zen-AI-Pentest Platform - MCP Page
// Model Context Protocol Management
// ============================================

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Plus,
  Zap,
  Server,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Settings,
  Trash2,
  RefreshCw,
  Brain,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { mcpApi } from '@/services/api';
import { toast } from 'sonner';
import type { MCPStatus } from '@/types';

// ============================================
// Status Badge Component
// ============================================
function StatusBadge({ status }: { status: MCPStatus }) {
  const config: Record<MCPStatus, { variant: string; icon: React.ElementType; label: string }> = {
    connected: { variant: 'success', icon: CheckCircle, label: 'Connected' },
    disconnected: { variant: 'secondary', icon: XCircle, label: 'Disconnected' },
    error: { variant: 'destructive', icon: AlertCircle, label: 'Error' },
  };

  const { variant, icon: Icon, label } = config[status];

  return (
    <Badge variant={variant as any} className="flex items-center gap-1 w-fit">
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
}

// ============================================
// Main MCP Page
// ============================================
export function MCPPage() {
  const queryClient = useQueryClient();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [serverToDelete, setServerToDelete] = useState<string | null>(null);
  const [newServer, setNewServer] = useState({ name: '', url: '' });

  // Fetch MCP servers
  const { data: servers, isLoading } = useQuery({
    queryKey: ['mcp-servers'],
    queryFn: () => mcpApi.getServers(),
  });

  // Mutations
  const registerMutation = useMutation({
    mutationFn: mcpApi.registerServer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
      toast.success('Server registered');
      setAddDialogOpen(false);
      setNewServer({ name: '', url: '' });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to register server');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: mcpApi.deleteServer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcp-servers'] });
      toast.success('Server removed');
      setDeleteDialogOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to remove server');
    },
  });

  const testMutation = useMutation({
    mutationFn: mcpApi.testConnection,
    onSuccess: (data) => {
      if (data.success) {
        toast.success(`Connection successful (${data.latency}ms)`);
      } else {
        toast.error('Connection failed');
      }
    },
    onError: () => {
      toast.error('Connection test failed');
    },
  });

  const handleRegister = () => {
    if (!newServer.name || !newServer.url) {
      toast.error('Please fill in all fields');
      return;
    }
    registerMutation.mutate(newServer);
  };

  const handleDelete = (id: string) => {
    setServerToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (serverToDelete) {
      deleteMutation.mutate(serverToDelete);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Model Context Protocol</h1>
          <p className="text-muted-foreground">
            Manage AI model integrations and capabilities
          </p>
        </div>
        <Button onClick={() => setAddDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Server
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-primary/10 to-secondary/10">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
              <Brain className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">What is MCP?</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Model Context Protocol (MCP) enables AI models to interact with external tools and services.
                Connect to AI providers like OpenAI, Anthropic, or custom models to enhance vulnerability analysis,
                report generation, and more.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Servers List */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {isLoading ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin" />
          </div>
        ) : servers && servers.length > 0 ? (
          servers.map((server) => (
            <Card key={server.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Server className="w-5 h-5 text-primary" />
                  </div>
                  <StatusBadge status={server.status} />
                </div>
                <CardTitle className="mt-4">{server.name}</CardTitle>
                <CardDescription className="font-mono text-xs">{server.url}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium mb-2">Capabilities</p>
                    <div className="flex flex-wrap gap-1">
                      {server.capabilities.slice(0, 3).map((cap) => (
                        <Badge key={cap.name} variant="secondary" className="text-xs">
                          {cap.name}
                        </Badge>
                      ))}
                      {server.capabilities.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{server.capabilities.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => testMutation.mutate(server.id)}
                      disabled={testMutation.isPending}
                    >
                      {testMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <Link to={`/mcp/${server.id}`}>
                        <Settings className="w-4 h-4" />
                      </Link>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(server.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No MCP servers configured</p>
            <Button className="mt-4" onClick={() => setAddDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Server
            </Button>
          </div>
        )}
      </div>

      {/* Add Server Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add MCP Server</DialogTitle>
            <DialogDescription>
              Register a new Model Context Protocol server
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Server Name</Label>
              <Input
                id="name"
                placeholder="e.g., OpenAI GPT-4"
                value={newServer.name}
                onChange={(e) => setNewServer({ ...newServer, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="url">Server URL</Label>
              <Input
                id="url"
                placeholder="e.g., https://api.openai.com/v1"
                value={newServer.url}
                onChange={(e) => setNewServer({ ...newServer, url: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRegister} disabled={registerMutation.isPending}>
              {registerMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Add Server
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Server</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this MCP server?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
